import { Component } from '@theme/component';

const ENDPOINT = 'https://judge.me/reviews/reviews_for_widget';
const PER_PAGE = 10;

/**
 * @typedef {object} JudgeMeReview
 * @property {number | string} rating
 * @property {string} title
 * @property {string} body_html
 * @property {string} created_at
 * @property {string} reviewer_name
 * @property {boolean} verified_buyer
 * @property {string | null} reply_content - HTML
 * @property {Array<Record<string, unknown>>} cf_answers
 * @property {Array<{ small?: string, original?: string }>} pictures_urls
 */

/**
 * @typedef {object} Refs
 * @property {HTMLUListElement} track
 * @property {HTMLTemplateElement} cardTemplate
 */

/**
 * Gate-passing products' review list (blocks/product-reviews.liquid): a swipeable row of
 * excerpts on the page, and every review in full in a drawer with "Load more" and
 * "Write a review".
 *
 * Reads Judge.me's public widget endpoint — the same read-only JSON their own widget loads, sent
 * with `Access-Control-Allow-Origin: *` — so no app embed or API token is involved. Review and
 * reply bodies arrive as HTML; only their text is ever put on the page.
 *
 * @extends {Component<Refs>}
 */
class ProductReviewsComponent extends Component {
  requiredRefs = ['track', 'cardTemplate'];

  /** @type {JudgeMeReview[]} */
  #reviews = [];
  #page = 0;
  #totalPages = 1;
  #total = 0;
  /** @type {Promise<void> | null} */
  #pending = null;
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#loadNextPage();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.#observer.observe(this);

    this.refs.track.addEventListener('click', this.#onTrackClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.refs.track.removeEventListener('click', this.#onTrackClick);
  }

  get #drawer() {
    return /** @type {(HTMLElement & { open: () => void }) | null} */ (
      document.getElementById(this.dataset.drawerId ?? '')
    );
  }

  /**
   * @param {string} selector
   * @returns {HTMLElement | null}
   */
  #inDrawer(selector) {
    return this.#drawer?.querySelector(selector) ?? null;
  }

  async openDrawer() {
    this.showList();
    this.#drawer?.open();
    if (!this.#page) await this.#loadNextPage();
  }

  loadMore() {
    this.#loadNextPage();
  }

  showForm() {
    this.#toggleView('form');
    this.#inDrawer('[data-drawer-view="form"] input, [data-drawer-view="form"] button')?.focus();
  }

  showList() {
    this.#toggleView('list');
  }

  /** @param {'list' | 'form'} view */
  #toggleView(view) {
    const drawer = this.#drawer;
    if (!drawer) return;

    for (const panel of drawer.querySelectorAll('[data-drawer-view]')) {
      panel.toggleAttribute('hidden', /** @type {HTMLElement} */ (panel).dataset.drawerView !== view);
    }
    this.#inDrawer('[data-drawer-footer]')?.toggleAttribute('hidden', view === 'form');
  }

  /** A card in the row opens the drawer scrolled to that same review. @param {MouseEvent} event */
  #onTrackClick = async (event) => {
    const card = event.target instanceof Element ? event.target.closest('.review-card') : null;
    if (!(card instanceof HTMLElement)) return;

    await this.openDrawer();
    this.#inDrawer(`[data-review-index="${card.dataset.reviewIndex}"]`)?.scrollIntoView({ block: 'start' });
  };

  #loadNextPage() {
    if (this.#pending || this.#page >= this.#totalPages) return this.#pending;

    this.#pending = this.#fetchPage(this.#page + 1)
      .then((reviews) => this.#append(reviews))
      .catch((error) => {
        this.#setStatus(this.dataset.labelError ?? '');
        console.warn('[product-reviews] could not load reviews:', error);
      })
      .finally(() => {
        this.#pending = null;
      });

    return this.#pending;
  }

  /**
   * @param {number} page
   * @returns {Promise<JudgeMeReview[]>}
   */
  async #fetchPage(page) {
    const { shopDomain = '', productId = '' } = this.dataset;
    const url = new URL(ENDPOINT);
    url.search = new URLSearchParams({
      url: shopDomain,
      shop_domain: shopDomain,
      platform: 'shopify',
      product_id: productId,
      page: String(page),
      per_page: String(PER_PAGE),
    }).toString();

    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();
    this.#page = data.pagination?.current_page ?? page;
    this.#totalPages = data.pagination?.total_pages ?? page;
    this.#total = Number(data.number_of_reviews) || 0;

    return Array.isArray(data.reviews) ? data.reviews : [];
  }

  /** @param {JudgeMeReview[]} reviews */
  #append(reviews) {
    const start = this.#reviews.length;
    this.#reviews.push(...reviews);

    const list = this.#inDrawer('[data-drawer-list]');
    reviews.forEach((review, offset) => {
      const index = start + offset;
      if (this.#page === 1) this.refs.track.append(this.#renderCard(review, index));
      list?.append(this.#renderCard(review, index));
    });

    const label = this.dataset.labelShowing ?? '';
    this.#setStatus(
      label.replace('[shown]', String(this.#reviews.length)).replace('[total]', String(this.#total || this.#reviews.length))
    );
    this.#inDrawer('[data-drawer-more]')?.toggleAttribute('hidden', this.#page >= this.#totalPages);
  }

  /** @param {string} text */
  #setStatus(text) {
    const status = this.#inDrawer('[data-drawer-status]');
    if (status) status.textContent = text;
  }

  /**
   * @param {JudgeMeReview} review
   * @param {number} index
   * @returns {HTMLElement}
   */
  #renderCard(review, index) {
    const fragment = /** @type {DocumentFragment} */ (this.refs.cardTemplate.content.cloneNode(true));
    const card = /** @type {HTMLElement} */ (fragment.firstElementChild);
    const field = (/** @type {string} */ name) => /** @type {HTMLElement} */ (card.querySelector(`[data-field="${name}"]`));

    card.dataset.reviewIndex = String(index);

    const rating = Number(review.rating) || 0;
    const stars = card.querySelector('.rating-stars');
    if (stars instanceof HTMLElement) {
      stars.style.setProperty('--rating-stars-fill', `${(rating / 5) * 100}%`);
      stars.setAttribute('aria-label', (this.dataset.labelRating ?? '').replace('[rating]', String(rating)));
    }

    const date = new Date(review.created_at);
    const dateField = /** @type {HTMLTimeElement} */ (field('date'));
    if (!Number.isNaN(date.getTime())) {
      dateField.dateTime = review.created_at;
      dateField.textContent = date.toLocaleDateString(this.dataset.locale || undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }

    setText(field('title'), review.title);
    field('body').replaceChildren(...htmlToParagraphs(review.body_html));
    setText(field('name'), review.reviewer_name);
    field('verified').hidden = review.verified_buyer !== true;

    this.#renderAnswers(field('answers'), review.cf_answers);
    this.#renderPhotos(field('photos'), review);

    const reply = review.reply_content ? htmlToParagraphs(review.reply_content) : [];
    field('reply').hidden = reply.length === 0;
    field('reply-body').replaceChildren(...reply);

    return card;
  }

  /**
   * Judge.me custom questions ("Fitting: True to size"). Their key names vary by form version,
   * so read the common ones and skip anything without both a question and an answer.
   * @param {HTMLElement} list
   * @param {Array<Record<string, unknown>>} answers
   */
  #renderAnswers(list, answers) {
    const rows = (Array.isArray(answers) ? answers : []).flatMap((answer) => {
      const question = answer.cf_question_title ?? answer.question ?? answer.title ?? answer.label;
      const value = answer.value ?? answer.answer ?? answer.values;
      const text = Array.isArray(value) ? value.join(', ') : value;
      if (typeof question !== 'string' || (typeof text !== 'string' && typeof text !== 'number')) return [];

      const term = document.createElement('dt');
      term.textContent = question;
      const detail = document.createElement('dd');
      detail.textContent = String(text);
      return [term, detail];
    });

    list.replaceChildren(...rows);
    list.hidden = rows.length === 0;
  }

  /**
   * @param {HTMLElement} list
   * @param {JudgeMeReview} review
   */
  #renderPhotos(list, review) {
    const alt = (this.dataset.labelPhoto ?? '').replace('[name]', review.reviewer_name ?? '');
    const items = (Array.isArray(review.pictures_urls) ? review.pictures_urls : []).flatMap((picture) => {
      if (!picture?.small || !picture.original) return [];

      const link = document.createElement('a');
      link.href = picture.original;
      link.target = '_blank';
      link.rel = 'noopener';
      const image = document.createElement('img');
      image.src = picture.small;
      image.alt = alt;
      image.loading = 'lazy';
      image.width = 64;
      image.height = 64;
      link.append(image);

      const item = document.createElement('li');
      item.append(link);
      return [item];
    });

    list.replaceChildren(...items);
    list.hidden = items.length === 0;
  }
}

/**
 * @param {HTMLElement} element
 * @param {string | undefined} text
 */
function setText(element, text) {
  element.textContent = text ?? '';
  element.hidden = !text;
}

/**
 * Turns review HTML into plain-text paragraphs. Parsed into an inert document and only
 * `textContent` is read back, so no markup or script from the review ever reaches the page.
 * @param {string} html
 * @returns {HTMLParagraphElement[]}
 */
function htmlToParagraphs(html) {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  const blocks = doc.body.querySelectorAll('p, li');
  const texts = blocks.length ? [...blocks].map((block) => block.textContent ?? '') : [doc.body.textContent ?? ''];

  return texts
    .map((text) => text.trim())
    .filter(Boolean)
    .map((text) => {
      const paragraph = document.createElement('p');
      paragraph.textContent = text;
      return paragraph;
    });
}

if (!customElements.get('product-reviews-component')) {
  customElements.define('product-reviews-component', ProductReviewsComponent);
}
